import { useIsSuperUser, usePermissionItem } from '@/features/access-control';
import type { PermissionItem } from '@/features/access-control';
import type { Permission } from '@/shared/constants';
import { Skeleton } from '@/shared/ui/skeleton';
import { Navigate, Outlet } from 'react-router';
import { paths } from '../router/path';

type PremiumGuardProps = {
	permission: Permission;
	/** When provided, redirects unless the resolved item has `company: true`. The `role` field on this item is not used. */
	buyPermission?: Permission;
	/** Lets the route through when `permission` is missing from the response; a present item with `role: false` still redirects. */
	allowMissingPermission?: boolean;
	children?: React.ReactNode;
};

/**
 * Determines whether a user can access a route based on the permission item type:
 * - basic:   requires both `company` and `role` (company must have it configured AND user must have the role)
 * - premium: requires only `role` (addon status is a UI concern handled by AddonGate)
 * - plain:   requires only `role`
 *
 * SuperUser does NOT bypass this check — if a company has `company: false` on the main
 * permission, everyone is redirected regardless of role.
 */
const hasRouteAccess = (item: PermissionItem | undefined): boolean => {
	if (!item) return false;
	switch (item.type) {
		case 'basic':
			return item.company && item.role;
		case 'premium':
			return item.role;
		case '':
			return item.role;
	}
};

const PremiumGuard = ({
	permission,
	buyPermission,
	allowMissingPermission = false,
	children,
}: PremiumGuardProps) => {
	const { item, isLoading: isLoadingRole } = usePermissionItem(permission);

	// Use the main permission as a no-op fallback so the hook call is always unconditional
	const { item: buyItem, isLoading: isLoadingBuy } = usePermissionItem(buyPermission ?? permission);
	// TODO: move SuperUser bypass to the API layer once the backend handles it
	const isSuperUser = useIsSuperUser();

	if (isLoadingRole || (buyPermission !== undefined && isLoadingBuy))
		return (
			<div className='flex flex-col gap-4 p-6 w-full'>
				<Skeleton
					height={28}
					width='35%'
					borderRadius={8}
				/>
				<Skeleton
					height={16}
					count={2}
					borderRadius={6}
				/>
				<Skeleton
					height={180}
					borderRadius={12}
				/>
			</div>
		);

	const skipRouteAccess = allowMissingPermission && item === undefined;

	if (!isSuperUser && !skipRouteAccess && !hasRouteAccess(item))
		return (
			<Navigate
				to={paths.home()}
				replace
			/>
		);

	if (!isSuperUser && buyPermission !== undefined) {
		const isCompanyEnabled = buyItem?.type === 'basic' && buyItem.company === true;

		if (!isCompanyEnabled)
			return (
				<Navigate
					to={paths.home()}
					replace
				/>
			);
	}

	if (children) return <>{children}</>;

	return <Outlet />;
};

export default PremiumGuard;
